import { useState, type FormEvent } from 'react';
import type { Client } from '../../api/client.js';
import { COULEURS, POLICE_SYSTEME } from '../../app/styles.js';
import { useRoleCourant } from '../../app/role-courant.js';
import { cheminEcran } from '../../app/ecrans.js';
import { Lien } from '../../app/navigation.js';
import { CATALOGUE_DEMARRAGE } from '../demarrage/catalogue.js';
import { AvertissementForcage } from '../demarrage/forcage.js';
import { CONDITION_MANDAT, NOTE_EXEMPLE, demanderDeblocageMandat, forcerMandat, lancerExport, soumettreAjout } from './actions.js';
import { estFratrie, parentsCommunsPossibles, type LienAjout, type SaisieAjout, type TypeLien } from './ajout-personne.js';
import { ArbreInteractif } from './arbre-navigable.js';
import { ArbreClient } from './client.js';
import { DISPOSITIONS } from './dispositions.js';
import { BoutonExportNotaire } from './export-notaire.js';
import { donneesExemple, HERITIER_EXEMPLE } from './exemple.js';
import { DEVOLUTION_NON_CALCULEE, STATUTS_DEVOLUTION } from './libelles.js';
import { nomAffiche, personneParId } from './modele.js';
import { phraseNotoriete } from './notoriete.js';
import { devolutionVide } from './source.js';
import { TableauDefunts, TableauVivants } from './tableaux.js';
import type { DonneesArbre, IdDisposition, StatutContact } from './types.js';

const bouton = {
  minHeight: 44,
  padding: '0 16px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.marine}`,
  background: COULEURS.blanc,
  color: COULEURS.marine,
  font: 'inherit',
  cursor: 'pointer',
} as const;

/** ADR 0064 : un parent commun non saisi est créé par le serveur, au statut inconnu ; sa part reste en réserve. */
const NOTE_PARENT_A_RECHERCHER =
  'Les parents communs non encore saisis seront ajoutés à l’arbre « à rechercher », au statut inconnu : leur part reste en réserve jusqu’à ce que leur état civil soit établi.';

/** Aucune donnée : jamais un exemple. Les conteneurs ne montent la page qu'avec des données lues ou le mode exemple explicite. */
const VIDE: DonneesArbre = { personnes: [], relations: [], successions: [], devolution: devolutionVide(), devolutionCalculee: false };

interface PropsEcran {
  donnees?: DonneesArbre;
  /** Mode développement (VITE_LAKOU_MOCKS) : la famille d'exemple tient lieu de données, sans export ni enregistrement. */
  exemple?: boolean;
  /** Client de l'API ; absent en mode exemple, où rien n'est enregistré. */
  client?: Pick<Client, 'appeler'>;
  dossierId?: string;
  /** Appelé après une écriture réussie : le conteneur relit l'arbre du dossier (ARB-GENEA-09). */
  onModifie?: () => void;
}

/** Statut du résultat juridique, posé près des quotes-parts (SYNTHESE §2 : « à valider notaire » tant que non validé). */
function MentionDevolution({ donnees }: { donnees: DonneesArbre }) {
  const { status } = donnees.devolution;
  if (donnees.devolutionCalculee === false) {
    return (
      <p data-statut-devolution="non_calculee" style={{ color: COULEURS.ardoise }}>
        {DEVOLUTION_NON_CALCULEE}
      </p>
    );
  }
  return (
    <p data-statut-devolution={status} style={{ color: COULEURS.ardoise }}>
      Quotes-parts : {STATUTS_DEVOLUTION[status].mention}
    </p>
  );
}

function telecharger(nom: string, contenu: string): void {
  const url = URL.createObjectURL(new Blob([String.fromCharCode(0xfeff), contenu], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  a.click();
  URL.revokeObjectURL(url);
}

function FormulaireAjout({
  donnees,
  client,
  dossierId,
  onFini,
  lienInitial,
}: {
  donnees: DonneesArbre;
  client?: PropsEcran['client'];
  dossierId: string;
  onFini: (message: string | null) => void;
  /** Bouton « Ajouter un frère ou une sœur » d'une carte : le lien est déjà choisi. */
  lienInitial?: LienAjout;
}) {
  const [saisie, setSaisie] = useState<SaisieAjout>({
    prenoms: '',
    nom: '',
    etat: 'vivant',
    anneeDeces: '',
    hypothese: false,
    ...(lienInitial ? { lien: lienInitial } : {}),
  });
  const [retour, setRetour] = useState<{ erreurs: Record<string, string>; message: string | null }>({ erreurs: {}, message: null });
  const modifier = <K extends keyof SaisieAjout>(cle: K, valeur: SaisieAjout[K]): void => setSaisie((s) => ({ ...s, [cle]: valeur }));
  const role = useRoleCourant();
  const [avertissement, setAvertissement] = useState(false);
  const soumettre = async (): Promise<boolean> => {
    const r = await soumettreAjout(saisie, client, dossierId, donnees);
    if (r.fini) onFini(r.message);
    else if (r.mandatRequis) setAvertissement(true);
    else setRetour({ erreurs: r.erreurs, message: r.message });
    return r.fini;
  };
  const envoyer = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    await soumettre();
  };
  const champ = { display: 'block', minHeight: 44, width: '100%', font: 'inherit', boxSizing: 'border-box' } as const;
  const parentsCommuns = saisie.lien ? parentsCommunsPossibles(donnees, saisie.lien.personneId) : [];
  return (
    <form
      onSubmit={(e) => void envoyer(e)}
      aria-label="Ajouter une personne"
      style={{ border: `1px solid ${COULEURS.bordure}`, borderRadius: 8, padding: 16, margin: '16px 0', maxWidth: 480 }}
    >
      <label>
        Prénoms
        <input style={champ} value={saisie.prenoms} onChange={(e) => modifier('prenoms', e.target.value)} />
      </label>
      <label>
        Nom de famille
        <input
          style={champ}
          value={saisie.nom}
          aria-invalid={Boolean(retour.erreurs['nom'])}
          onChange={(e) => modifier('nom', e.target.value)}
        />
      </label>
      {retour.erreurs['nom'] ? <p role="alert">{retour.erreurs['nom']}</p> : null}
      <label>
        Situation
        <select style={champ} value={saisie.etat} onChange={(e) => modifier('etat', e.target.value as SaisieAjout['etat'])}>
          <option value="vivant">Vivante</option>
          <option value="decede">Décédée</option>
          <option value="inconnu">Situation inconnue</option>
        </select>
      </label>
      {saisie.etat === 'decede' ? (
        <label>
          Année du décès (ou date complète, aaaa-mm-jj)
          <input
            style={champ}
            value={saisie.anneeDeces}
            aria-invalid={Boolean(retour.erreurs['anneeDeces'])}
            onChange={(e) => modifier('anneeDeces', e.target.value)}
          />
        </label>
      ) : null}
      {retour.erreurs['anneeDeces'] ? <p role="alert">{retour.erreurs['anneeDeces']}</p> : null}
      <label>
        Lien avec
        <select
          style={champ}
          value={saisie.lien?.personneId ?? ''}
          onChange={(e) =>
            modifier('lien', e.target.value ? { type: saisie.lien?.type ?? 'enfant_de', personneId: e.target.value } : undefined)
          }
        >
          <option value="">Aucun lien pour l’instant</option>
          {donnees.personnes.map((p) => (
            <option key={p.id} value={p.id}>
              {nomAffiche(p)}
            </option>
          ))}
        </select>
      </label>
      {saisie.lien ? (
        <label>
          Cette personne est
          <select
            style={champ}
            value={saisie.lien.type}
            onChange={(e) => modifier('lien', { type: e.target.value as TypeLien, personneId: saisie.lien!.personneId })}
          >
            <option value="enfant_de">l’enfant de cette personne</option>
            <option value="parent_de">le parent de cette personne</option>
            <option value="conjoint_de">le conjoint de cette personne</option>
            <option value="frere_soeur_de">le frère ou la sœur de cette personne (mêmes parents)</option>
            <option value="demi_frere_soeur_de">le demi-frère ou la demi-sœur de cette personne (un seul parent commun)</option>
          </select>
        </label>
      ) : null}
      {saisie.lien?.type === 'demi_frere_soeur_de' && parentsCommuns.length > 1 ? (
        <label>
          Parent commun
          <select
            name="parentCommun"
            style={champ}
            value={saisie.lien.parentCommunId ?? ''}
            aria-invalid={Boolean(retour.erreurs['parentCommun'])}
            onChange={(e) =>
              modifier('lien', {
                type: 'demi_frere_soeur_de',
                personneId: saisie.lien!.personneId,
                ...(e.target.value ? { parentCommunId: e.target.value } : {}),
              })
            }
          >
            <option value="">Choisir le parent commun</option>
            {parentsCommuns.map((id) => {
              const p = personneParId(donnees, id);
              return p ? (
                <option key={id} value={id}>
                  {nomAffiche(p)}
                </option>
              ) : null;
            })}
          </select>
        </label>
      ) : null}
      {retour.erreurs['parentCommun'] ? <p role="alert">{retour.erreurs['parentCommun']}</p> : null}
      {estFratrie(saisie.lien) && parentsCommuns.length < (saisie.lien?.type === 'frere_soeur_de' ? 2 : 1) ? (
        <p style={{ color: COULEURS.ardoise }}>{NOTE_PARENT_A_RECHERCHER}</p>
      ) : null}
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44 }}>
        <input type="checkbox" checked={saisie.hypothese ?? false} onChange={(e) => modifier('hypothese', e.target.checked)} />
        Personne supposée, à confirmer
      </label>
      {retour.message ? <p role="status">{retour.message}</p> : null}
      <button type="submit" style={bouton}>
        Enregistrer
      </button>
      {avertissement && client ? (
        // Couloir G, point 5 (décision de Joel du 30/10) : jamais bloqué ; forçage tracé par la dirigeante, ou demande de déblocage.
        <AvertissementForcage
          conditions={[CONDITION_MANDAT]}
          role={role}
          dirigeante={CATALOGUE_DEMARRAGE.intervenants.dirigeante}
          annuler={() => setAvertissement(false)}
          forcer={async (motif) => {
            if (!(await forcerMandat(client, dossierId, motif))) return false;
            setAvertissement(false);
            await soumettre();
            return true;
          }}
          prevenir={() => demanderDeblocageMandat(client, dossierId)}
          remplir={
            <Lien vers={`${cheminEcran('D09')}?dossier=${encodeURIComponent(dossierId)}`} style={bouton} enfants="Envoyer le mandat" />
          }
        />
      ) : null}
    </form>
  );
}

/** D04 : l'arbre, les deux tableaux, l'ajout d'une personne et l'export pour le notaire. */
export function PageArbre({
  ecran,
  donnees,
  exemple = false,
  client,
  dossierId = 'exemple',
  onModifie,
}: PropsEcran & { ecran: { id: string; titre: string } }) {
  const d = donnees ?? (exemple ? donneesExemple() : VIDE);
  const [ajout, setAjout] = useState(false);
  // Lien pré-choisi par le bouton « Ajouter un frère ou une sœur » d'une carte ; la clé remonte le formulaire à chaque ouverture.
  const [lienInitial, setLienInitial] = useState<{ lien: LienAjout; cle: number } | null>(null);
  const ajouterFratrie = (personneId: string): void => {
    setLienInitial((l) => ({ lien: { type: 'frere_soeur_de', personneId }, cle: (l?.cle ?? 0) + 1 }));
    setAjout(true);
  };
  const [message, setMessage] = useState<string | null>(null);
  // Le vrai arbre dessiné par défaut ; la liste imbriquée reste accessible (lecteurs d'écran, petits écrans).
  const [affichage, setAffichage] = useState<'dessine' | 'liste'>('dessine');
  const notoriete = phraseNotoriete(d);
  const exporter = async (): Promise<void> => setMessage(await lancerExport(d, client, dossierId, telecharger));
  return (
    <section data-ecran={ecran.id} style={{ fontFamily: POLICE_SYSTEME }}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{ecran.titre}</h1>
      {donnees ? null : <p style={{ color: COULEURS.ardoise }}>{NOTE_EXEMPLE}</p>}
      <h2 style={{ fontSize: 20, color: COULEURS.marine }}>Qui hérite de qui</h2>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={bouton}
          aria-expanded={ajout}
          onClick={() => {
            setLienInitial(null);
            setAjout((v) => !v);
          }}
        >
          Ajouter une personne
        </button>
        {client ? <BoutonExportNotaire exporter={exporter} /> : null}
      </div>
      {message ? <p role="status">{message}</p> : null}
      {ajout ? (
        <FormulaireAjout
          key={lienInitial?.cle ?? 0}
          {...(lienInitial ? { lienInitial: lienInitial.lien } : {})}
          donnees={d}
          {...(client ? { client } : {})}
          dossierId={dossierId}
          onFini={(avertissement) => {
            setAjout(false);
            setMessage(avertissement);
            onModifie?.();
          }}
        />
      ) : null}
      <MentionDevolution donnees={d} />
      {notoriete ? (
        <p data-notoriete style={{ fontWeight: 600 }}>
          {notoriete}
        </p>
      ) : null}
      <div role="group" aria-label="Affichage de l’arbre" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
        {(
          [
            ['dessine', 'Arbre dessiné'],
            ['liste', 'Liste'],
          ] as const
        ).map(([id, libelle]) => (
          <button
            key={id}
            type="button"
            aria-pressed={affichage === id}
            onClick={() => setAffichage(id)}
            style={{
              ...bouton,
              ...(affichage === id ? { background: COULEURS.marine, color: COULEURS.blanc } : {}),
            }}
          >
            {libelle}
          </button>
        ))}
      </div>
      <ArbreInteractif key={affichage} donnees={d} disposition={affichage} onOuvrir={() => undefined} onAjouterFratrie={ajouterFratrie} />
      <TableauVivants donnees={d} />
      <TableauDefunts donnees={d} />
    </section>
  );
}

/** D04b : la même famille en cinq dispositions. */
export function PageDispositions({ ecran, donnees, exemple = false }: PropsEcran & { ecran: { id: string; titre: string } }) {
  const d = donnees ?? (exemple ? donneesExemple() : VIDE);
  const [disposition, setDisposition] = useState<IdDisposition>('classique');
  const [quotesParts, setQuotesParts] = useState(true);
  const [defuntsGrises, setDefuntsGrises] = useState(true);
  const [heritierId, setHeritierId] = useState<string>('');
  const courante = DISPOSITIONS.find((x) => x.id === disposition);
  return (
    <section data-ecran={ecran.id} style={{ fontFamily: POLICE_SYSTEME }}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{ecran.titre}</h1>
      {donnees ? null : <p style={{ color: COULEURS.ardoise }}>{NOTE_EXEMPLE}</p>}
      <div role="group" aria-label="Disposition de l’arbre" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {DISPOSITIONS.map((x) => (
          <button
            key={x.id}
            type="button"
            aria-pressed={x.id === disposition}
            onClick={() => setDisposition(x.id)}
            style={{
              ...bouton,
              borderRadius: 999,
              background: x.id === disposition ? COULEURS.marine : COULEURS.blanc,
              color: x.id === disposition ? COULEURS.blanc : COULEURS.marine,
            }}
          >
            {x.libelle}
          </button>
        ))}
      </div>
      <p>{courante?.aide}</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ minHeight: 44, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={quotesParts} onChange={(e) => setQuotesParts(e.target.checked)} />
          Afficher les quotes-parts
        </label>
        <label style={{ minHeight: 44, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={defuntsGrises} onChange={(e) => setDefuntsGrises(e.target.checked)} />
          Griser les défunts
        </label>
        {disposition === 'chemin' ? (
          <label>
            Héritier{' '}
            <select style={{ minHeight: 44 }} value={heritierId} onChange={(e) => setHeritierId(e.target.value)}>
              <option value="">Choisir un héritier</option>
              {d.personnes
                .filter((p) => p.vitalStatus === 'vivant' && !p.isHypothesis)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {nomAffiche(p)}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
      </div>
      {quotesParts ? <MentionDevolution donnees={d} /> : null}
      <ArbreInteractif
        key={disposition}
        donnees={d}
        disposition={disposition}
        afficher={{ quotesParts, defuntsGrises }}
        {...(heritierId ? { heritierId } : {})}
        onOuvrir={() => undefined}
      />
    </section>
  );
}

/** X15 : « Mon arbre », portail client. Les données viennent de l'API ; l'exemple n'est qu'un mode de développement explicite. */
export function PageMonArbre({
  ecran,
  donnees,
  exemple = false,
  viewerId = exemple ? HERITIER_EXEMPLE : '',
  statutsPortail,
}: PropsEcran & {
  ecran: { id: string; titre: string };
  exemple?: boolean;
  viewerId?: string;
  statutsPortail?: Record<string, StatutContact>;
}) {
  const d = donnees ?? (exemple ? donneesExemple() : null);
  return (
    <section data-ecran={ecran.id} style={{ fontFamily: POLICE_SYSTEME, fontSize: 18 }}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{ecran.titre}</h1>
      {exemple && !donnees ? <p style={{ color: COULEURS.ardoise }}>{NOTE_EXEMPLE}</p> : null}
      {d && d.personnes.length > 0 ? (
        <>
          <p>Voici votre famille. Seules les coordonnées que la personne a acceptées de partager apparaissent.</p>
          <ArbreClient donnees={d} viewerId={viewerId} {...(statutsPortail ? { statutsPortail } : {})} />
        </>
      ) : (
        <p role="status">
          Nous recherchons encore les membres de votre branche. Le Cabinet MAAT vous préviendra dès qu’ils seront identifiés.
        </p>
      )}
    </section>
  );
}
